import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search, Loader2, ArrowRight, CheckCircle2, Circle,
  AlertTriangle, Users, MessageSquare, Repeat2, Quote, Globe,
  TrendingUp, BarChart3, Shield, Bot, Brain, Eye, Zap,
  Network, MapPin, Hash, ExternalLink, ChevronRight,
  Activity, Target, Fingerprint, Newspaper, Star, AlertCircle,
  Play, SkipForward, XCircle, Clock, Link2,
  FileText, Crosshair, Type, Trash2, RefreshCw,
  ChevronDown, ChevronUp, History, LayoutGrid, Layers, CircleDot,
  ScanSearch, Locate
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import ReactFlow, {
  Background, Controls, MiniMap, useNodesState, useEdgesState,
  MarkerType, Handle, Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { apiClient } from '@/lib/api';
import { getMediaUrlsFromPost, PostMediaGrid } from '@/components/PostMedia';
import { useToast } from '@/hooks/use-toast';

// ─── Types ───────────────────────────────────────────────────────────

interface EventInvestigation {
  investigation_id: string;
  investigation_type: string;
  post_id: string;
  seed_tweet: Record<string, any>;
  timestamp: string;
  phases_completed: number[];
  nodes: any[];
  edges: any[];
  metrics: Record<string, any>;
  thread_tweets: any[];
  replies: any[];
  quotes: any[];
  retweeters: any[];
  bot_detection?: Record<string, any>;
  synthesis?: Record<string, any>;
}

interface PhaseConfig {
  id: number;
  name: string;
  description: string;
  icon: React.ReactNode;
}

// ─── Constants ───────────────────────────────────────────────────────

const PHASES: PhaseConfig[] = [
  { id: 1, name: 'Scoping', description: 'Collecting seed tweet, replies, quotes, retweeters', icon: <Target className="w-4 h-4" /> },
  { id: 2, name: 'Volume', description: 'Analyzing reach, news coverage, trends', icon: <TrendingUp className="w-4 h-4" /> },
  { id: 3, name: 'Sentiment & Geo', description: 'Sentiment scoring, country clustering, language analysis', icon: <Globe className="w-4 h-4" /> },
  { id: 4, name: 'Attribution', description: 'Identifying suspects, coordination, follow networks', icon: <Fingerprint className="w-4 h-4" /> },
  { id: 5, name: 'Synthesis', description: 'AI-powered analysis and intelligence report', icon: <Brain className="w-4 h-4" /> },
];

const NEON = '#00ff00';
const NEON_DIM = 'rgba(0,255,0,0.3)';
const NEON_GLOW = 'rgba(0,255,0,0.15)';

const NODE_COLORS: Record<string, string> = {
  user: '#00ff00',
  post: '#00aaff',
  country: '#ffaa00',
  narrative: '#ff00ff',
  community: '#ff6600',
  space: '#aa00ff',
};

const EDGE_COLORS: Record<string, string> = {
  reply: '#00aaff',
  quote: '#ffaa00',
  retweet: '#00ff00',
  mention: '#ff00ff',
  authored: '#666666',
  located_in: '#ffaa00',
  follows: '#00ff88',
};

const SENTIMENT_COLORS = {
  positive: '#00ff00',
  negative: '#ff4444',
  neutral: '#888888',
  mixed: '#ffaa00',
};

// ─── Custom React Flow Nodes ─────────────────────────────────────────

function UserNode({ data }: { data: any }) {
  const isAuthor = data.isAuthor;
  const borderColor = isAuthor ? NEON : (data.botScore > 0.6 ? '#ff4444' : '#444');
  return (
    <div
      className="relative px-3 py-2 rounded-lg border text-xs max-w-[160px]"
      style={{
        background: 'rgba(10,10,10,0.95)',
        borderColor,
        boxShadow: isAuthor ? `0 0 15px ${NEON_GLOW}` : 'none',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: borderColor, width: 6, height: 6 }} />
      <div className="flex items-center gap-1.5 mb-1">
        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,255,0,0.15)' }}>
          <Users className="w-3 h-3" style={{ color: NEON }} />
        </div>
        <span className="font-semibold truncate" style={{ color: isAuthor ? NEON : '#fff' }}>
          {data.label}
        </span>
      </div>
      {data.username && (
        <div className="text-[10px] opacity-60 truncate">@{data.username}</div>
      )}
      {data.botScore > 0 && (
        <div className="mt-1">
          <Badge variant="outline" className="text-[9px] px-1 py-0" style={{ borderColor: data.botScore > 0.6 ? '#ff4444' : '#ffaa00', color: data.botScore > 0.6 ? '#ff4444' : '#ffaa00' }}>
            Bot: {(data.botScore * 100).toFixed(0)}%
          </Badge>
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: borderColor, width: 6, height: 6 }} />
    </div>
  );
}

function PostNode({ data }: { data: any }) {
  return (
    <div
      className="px-3 py-2 rounded-lg border text-xs max-w-[180px]"
      style={{ background: 'rgba(10,10,30,0.95)', borderColor: '#00aaff' }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#00aaff', width: 6, height: 6 }} />
      <div className="flex items-center gap-1.5 mb-1">
        <MessageSquare className="w-3 h-3 text-blue-400" />
        <span className="font-semibold text-blue-400">Tweet</span>
      </div>
      <div className="text-[10px] opacity-70 line-clamp-2">{data.text || data.label}</div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#00aaff', width: 6, height: 6 }} />
    </div>
  );
}

function CountryNode({ data }: { data: any }) {
  return (
    <div
      className="px-3 py-2 rounded-lg border text-xs"
      style={{ background: 'rgba(30,20,0,0.95)', borderColor: '#ffaa00' }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#ffaa00', width: 6, height: 6 }} />
      <div className="flex items-center gap-1.5">
        <Globe className="w-3 h-3 text-yellow-400" />
        <span className="font-semibold text-yellow-400">{data.label}</span>
      </div>
      {data.userCount && (
        <div className="text-[10px] opacity-60 mt-0.5">{data.userCount} users</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: '#ffaa00', width: 6, height: 6 }} />
    </div>
  );
}

const nodeTypes = { user: UserNode, post: PostNode, country: CountryNode };

// ─── Formatters ──────────────────────────────────────────────────────

function formatNumber(n: number | undefined | null): string {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
}

interface ReportSection {
  title: string;
  content: string;
  bullets: string[];
  icon: string;
}

function parseAIReport(raw: string): ReportSection[] {
  if (!raw) return [];
  const sections: ReportSection[] = [];
  // Normalize: strip ### and extra newlines so numbered headers are consistent
  let text = raw.replace(/^#{1,6}\s*/gm, '').replace(/\n{3,}/g, '\n\n').trim();
  // Match "1. **TITLE**", "1. TITLE", and "1. TITLE" after ### strip
  const sectionPattern = /(?:^|\n)\s*(\d+)\.\s*(?:\*\*)?([A-Z][A-Z &/\-]+[A-Z])(?:\*\*)?\s*/g;
  const matches = [...text.matchAll(sectionPattern)];
  const iconMap: Record<string, string> = {
    'executive summary': 'summary',
    'key findings': 'findings',
    'sentiment analysis': 'sentiment',
    'sentiment': 'sentiment',
    'attribution assessment': 'attribution',
    'attribution': 'attribution',
    'bot detection': 'bot',
    'media coverage': 'media',
    'geographic': 'geo',
    'geo': 'geo',
    'coordination': 'coordination',
    'recommendations': 'recommendations',
    'conclusion': 'conclusion',
    'risk assessment': 'risk',
    'risk': 'risk',
    'network': 'network',
    'amplification': 'network',
  };

  for (let i = 0; i < matches.length; i++) {
    const title = matches[i][2].trim();
    const start = matches[i].index! + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index! : text.length;
    const body = text.slice(start, end).trim();

    const lines = body.split('\n').map(l => l.trim()).filter(Boolean);
    const bullets: string[] = [];
    const contentLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('- ') || line.startsWith('• ')) {
        bullets.push(line.replace(/^[-•]\s*/, ''));
      } else if (/^\d+\.\s*(?:\*\*)?[A-Z][A-Z &/\-]+[A-Z]/.test(line)) {
        break;
      } else {
        contentLines.push(line);
      }
    }

    const lowerTitle = title.toLowerCase();
    let icon = 'default';
    for (const [key, val] of Object.entries(iconMap)) {
      if (lowerTitle.includes(key)) { icon = val; break; }
    }

    sections.push({ title: title.charAt(0) + title.slice(1).toLowerCase(), content: contentLines.join('\n'), bullets, icon });
  }

  if (sections.length === 0 && text.length > 0) {
    // Fallback: split by ### or ## headers if present
    const hashSplits = text.split(/(?=^#{1,3}\s+.+$)/gm).filter(Boolean);
    if (hashSplits.length > 1) {
      hashSplits.forEach((block) => {
        const firstLine = block.split('\n')[0]?.trim() || '';
        const title = firstLine.replace(/^#+\s*/, '').trim();
        const content = block.slice(firstLine.length).trim();
        if (title) sections.push({ title, content, bullets: [], icon: 'default' });
      });
    }
    if (sections.length === 0) {
      sections.push({ title: 'Analysis', content: text, bullets: [], icon: 'summary' });
    }
  }
  return sections;
}

function buildWordCloud(texts: string[]): { text: string; weight: number }[] {
  const stopWords = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'shall', 'can', 'need', 'dare', 'ought', 'used', 'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'out', 'off', 'over', 'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just', 'because', 'but', 'and', 'or', 'if', 'while', 'about', 'up', 'its', 'it', 'this', 'that', 'these', 'those', 'he', 'she', 'they', 'we', 'you', 'i', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'our', 'their', 'what', 'which', 'who', 'whom', 'whose', 'rt', 'https', 'http', 'amp', 'co', 'via', 'like', 'get', 'got', 'going', 'one', 'two', 'also', 'new', 'make', 'still', 'even', 'back', 'much', 'way', 'don', 'know', 'well', 'think', 'say', 'said', 'people', 'now', 'time', 'see', 'come', 'right', 'look', 'want']);
  const freq: Record<string, number> = {};
  for (const text of texts) {
    const words = text.toLowerCase().replace(/[^a-z0-9@#\s]/g, ' ').split(/\s+/);
    for (const w of words) {
      if (w.length < 3 || stopWords.has(w) || /^\d+$/.test(w)) continue;
      freq[w] = (freq[w] || 0) + 1;
    }
  }
  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 60)
    .map(([text, weight]) => ({ text, weight }));
}

function extractPostId(input: string): string {
  const trimmed = input.trim();
  const urlMatch = trimmed.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
  if (urlMatch) return urlMatch[1];
  if (/^\d+$/.test(trimmed)) return trimmed;
  return trimmed;
}

// ─── Metric Card ─────────────────────────────────────────────────────

function MetricCard({ label, value, icon, color = NEON, subtitle }: {
  label: string; value: string | number; icon: React.ReactNode; color?: string; subtitle?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl border p-4"
      style={{ background: 'rgba(10,10,10,0.8)', borderColor: `${color}33` }}
    >
      <div className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-5" style={{ background: color, filter: 'blur(20px)' }} />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-50 mb-1">{label}</p>
          <p className="text-2xl font-bold" style={{ color }}>{typeof value === 'number' ? formatNumber(value) : value}</p>
          {subtitle && <p className="text-[11px] opacity-40 mt-0.5">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}15` }}>
          {icon}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Phase Stepper ───────────────────────────────────────────────────

function PhaseStepper({ phases, completedPhases, activePhase, onRunPhase }: {
  phases: PhaseConfig[];
  completedPhases: number[];
  activePhase: number | null;
  onRunPhase: (phase: number) => void;
}) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {phases.map((phase, idx) => {
        const isCompleted = completedPhases.includes(phase.id);
        const isActive = activePhase === phase.id;
        const isNext = !isCompleted && !isActive && (phase.id === 1 || completedPhases.includes(phase.id - 1));

        return (
          <div key={phase.id} className="flex items-center gap-2">
            <motion.button
              onClick={() => isNext ? onRunPhase(phase.id) : undefined}
              whileHover={isNext ? { scale: 1.03 } : {}}
              whileTap={isNext ? { scale: 0.97 } : {}}
              className={`relative flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all min-w-[180px] ${
                isNext ? 'cursor-pointer hover:border-green-400' : isCompleted ? '' : 'opacity-50 cursor-not-allowed'
              }`}
              style={{
                background: isActive ? 'rgba(0,255,0,0.08)' : isCompleted ? 'rgba(0,255,0,0.04)' : 'rgba(20,20,20,0.8)',
                borderColor: isActive ? NEON : isCompleted ? NEON_DIM : '#333',
                boxShadow: isActive ? `0 0 20px ${NEON_GLOW}` : 'none',
              }}
            >
              {isActive && (
                <motion.div
                  className="absolute inset-0 rounded-xl"
                  style={{ border: `1px solid ${NEON}` }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              )}
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5" style={{ color: NEON }} />
                ) : isActive ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                    <Loader2 className="w-5 h-5" style={{ color: NEON }} />
                  </motion.div>
                ) : isNext ? (
                  <Play className="w-5 h-5 text-green-400" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-600" />
                )}
              </div>
              <div className="text-left">
                <div className="text-xs font-semibold" style={{ color: isCompleted || isActive ? NEON : '#888' }}>
                  Phase {phase.id}: {phase.name}
                </div>
                <div className="text-[10px] opacity-50 mt-0.5">{phase.description}</div>
              </div>
            </motion.button>
            {idx < phases.length - 1 && (
              <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: isCompleted ? NEON : '#333' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Seed Tweet Card ─────────────────────────────────────────────────

function SeedTweetCard({ tweet }: { tweet: Record<string, any> }) {
  if (!tweet || Object.keys(tweet).length === 0) return null;
  const author = tweet.author || {};
  const text = tweet.text || tweet.fullText || '';
  const created = tweet.createdAt || tweet.created_at || '';
  const likes = tweet.likeCount ?? tweet.likes ?? 0;
  const retweets = tweet.retweetCount ?? tweet.retweets ?? 0;
  const replies = tweet.replyCount ?? tweet.replies ?? 0;
  const quotes = tweet.quoteCount ?? tweet.quotes ?? 0;
  const views = tweet.viewCount ?? tweet.views ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border p-5"
      style={{ background: 'rgba(10,10,10,0.9)', borderColor: NEON_DIM }}
    >
      <div className="flex items-start gap-3 mb-3">
        {author.profilePicture && (
          <img src={author.profilePicture} alt="" className="w-10 h-10 rounded-full border" style={{ borderColor: NEON_DIM }} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm" style={{ color: NEON }}>{author.name || 'Unknown'}</span>
            {author.isBlueVerified && <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />}
            <span className="text-xs opacity-40">@{author.userName || author.username || '?'}</span>
          </div>
          {created && <span className="text-[10px] opacity-30">{new Date(created).toLocaleString()}</span>}
        </div>
        <a
          href={tweet.url || `https://x.com/i/status/${tweet.id || tweet.tweetId}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs flex items-center gap-1 opacity-40 hover:opacity-80 transition-opacity"
        >
          <ExternalLink className="w-3 h-3" /> View
        </a>
      </div>
      <p className="text-sm leading-relaxed mb-4 whitespace-pre-wrap">{text}</p>
      <PostMediaGrid mediaUrls={getMediaUrlsFromPost(tweet)} className="mb-4" />
      <div className="flex items-center gap-5 text-xs opacity-60">
        <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{formatNumber(replies)}</span>
        <span className="flex items-center gap-1"><Repeat2 className="w-3.5 h-3.5" />{formatNumber(retweets)}</span>
        <span className="flex items-center gap-1"><Quote className="w-3.5 h-3.5" />{formatNumber(quotes)}</span>
        <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" />{formatNumber(likes)}</span>
        {views > 0 && <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{formatNumber(views)}</span>}
      </div>
    </motion.div>
  );
}

// ─── Suspect Row ─────────────────────────────────────────────────────

function SuspectRow({ suspect, rank }: { suspect: any; rank: number }) {
  const score = suspect.bot_score ?? suspect.botScore ?? 0;
  const riskColor = score > 0.7 ? '#ff4444' : score > 0.4 ? '#ffaa00' : '#00ff00';
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.05 }}
      className="flex items-center gap-3 px-4 py-3 rounded-lg border"
      style={{ background: 'rgba(15,15,15,0.9)', borderColor: '#222' }}
    >
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: `${riskColor}20`, color: riskColor }}>
        {rank + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{suspect.name || suspect.username || 'Unknown'}</span>
          {suspect.username && <span className="text-[10px] opacity-40">@{suspect.username}</span>}
        </div>
        {suspect.reasons && suspect.reasons.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {suspect.reasons.slice(0, 3).map((r: string, i: number) => (
              <Badge key={i} variant="outline" className="text-[9px] px-1.5 py-0" style={{ borderColor: '#333', color: '#888' }}>{r}</Badge>
            ))}
          </div>
        )}
      </div>
      <div className="text-right">
        <div className="text-xs font-mono font-bold" style={{ color: riskColor }}>{(score * 100).toFixed(0)}%</div>
        <div className="text-[9px] opacity-40">risk</div>
      </div>
      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: '#222' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${score * 100}%`, background: riskColor }} />
      </div>
    </motion.div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export default function XEventInvestigation() {
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [investigation, setInvestigation] = useState<EventInvestigation | null>(null);
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [graphFilter, setGraphFilter] = useState<string>('all');
  const [graphEdgeFilter, setGraphEdgeFilter] = useState<string>('all');
  const [graphHighlight, setGraphHighlight] = useState<string>('none');
  const [layoutSeed, setLayoutSeed] = useState(0);
  const [graphLayout, setGraphLayout] = useState<'circular' | 'radial' | 'hierarchical' | 'grid'>('circular');
  const [sentimentFilter, setSentimentFilter] = useState<string>('all');
  const [botsOnly, setBotsOnly] = useState(false);
  const [similarNarratives, setSimilarNarratives] = useState<any>(null);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const reactFlowRef = useRef<any>(null);
  const [runningAll, setRunningAll] = useState(false);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reportExporting, setReportExporting] = useState(false);

  // ─── History ────────────────────────────────────────────────────

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await apiClient.listEventInvestigations(30) as any;
      setHistoryItems(data?.investigations || []);
    } catch { /* ignore */ }
    setHistoryLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const loadHistoryEntry = useCallback(async (item: any) => {
    setError(null);
    setActivePhase(1);
    try {
      const result = await apiClient.getEventInvestigation(item.investigation_id) as EventInvestigation;
      setInvestigation(result);
      setActivePhase(null);
      setActiveTab('overview');
      setInput(item.post_id || '');
    } catch (e: any) {
      setError(e.message || 'Failed to load investigation');
      setActivePhase(null);
    }
  }, []);

  const deleteHistoryEntry = useCallback(async (investigationId: string) => {
    try {
      await apiClient.deleteEventInvestigation(investigationId);
      setHistoryItems(prev => prev.filter(h => h.investigation_id !== investigationId));
    } catch { /* ignore */ }
  }, []);

  // ─── Report Export ──────────────────────────────────────────────

  const exportReport = useCallback(async () => {
    if (!investigation) return;
    setReportExporting(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/twitter/investigate/event/${investigation.investigation_id}/report/pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'PDF generation failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const author = investigation.seed_tweet?.author?.userName || 'event';
      a.download = `sycek_event_report_${author}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Failed to generate PDF report');
    } finally {
      setReportExporting(false);
    }
  }, [investigation]);

  // ─── Phase Runner ────────────────────────────────────────────────

  const runPhase = useCallback(async (phase: number, inv?: EventInvestigation | null) => {
    const current = inv ?? investigation;
    if (!current && phase !== 1) return;

    setActivePhase(phase);
    setError(null);

    try {
      let result: any;
      if (phase === 1) {
        const postId = extractPostId(input);
        if (!postId) throw new Error('Please enter a valid Tweet URL or ID');
        result = await apiClient.startEventInvestigation({
          post_id_or_url: postId,
          max_reply_pages: 3,
          max_quote_pages: 3,
          max_retweeter_pages: 2,
          include_thread: true,
        });
      } else {
        let retries = 3;
        while (retries > 0) {
          try {
            result = await apiClient.runEventPhase(current!.investigation_id, phase);
            break;
          } catch (retryErr: any) {
            if (retryErr.message?.includes('not found') || retryErr.message?.includes('Not Found')) {
              retries--;
              if (retries > 0) {
                await new Promise(r => setTimeout(r, 2000));
                continue;
              }
            }
            throw retryErr;
          }
        }
      }
      setInvestigation(result);
      return result;
    } catch (err: any) {
      setError(err.message || `Phase ${phase} failed`);
      return null;
    } finally {
      setActivePhase(null);
    }
  }, [investigation, input]);

  const runBotDetection = useCallback(async () => {
    if (!investigation) return;
    setActivePhase(6);
    setError(null);
    try {
      const botResult = await apiClient.runEventBotDetection(investigation.investigation_id) as Record<string, any>;
      setInvestigation(prev => prev ? { ...prev, bot_detection: botResult } : prev);
    } catch (err: any) {
      setError(err.message || 'Bot detection failed');
    } finally {
      setActivePhase(null);
    }
  }, [investigation]);

  const runAllPhases = useCallback(async () => {
    setRunningAll(true);
    setError(null);
    try {
      let inv = await runPhase(1);
      if (!inv) return;
      for (const phase of [2, 3, 4, 5]) {
        inv = await runPhase(phase, inv);
        if (!inv) return;
      }
      setInvestigation(inv);
      setActivePhase(6);
      try {
        const botResult = await apiClient.runEventBotDetection(inv.investigation_id) as Record<string, any>;
        setInvestigation(prev => prev ? { ...prev, bot_detection: botResult } : prev);
      } catch { /* bot detection is optional */ }
    } finally {
      setActivePhase(null);
      setRunningAll(false);
      fetchHistory();
    }
  }, [runPhase, fetchHistory]);

  const handleStart = useCallback(() => {
    if (!input.trim()) {
      setError('Please enter a Tweet URL or ID');
      return;
    }
    setInvestigation(null);
    runPhase(1);
  }, [input, runPhase]);

  const handleRunAll = useCallback(() => {
    if (!input.trim()) {
      setError('Please enter a Tweet URL or ID');
      return;
    }
    setInvestigation(null);
    runAllPhases();
  }, [input, runAllPhases]);

  const handlePhaseClick = useCallback((phase: number) => {
    if (phase === 1) {
      handleStart();
    } else {
      runPhase(phase);
    }
  }, [handleStart, runPhase]);

  // ─── Derived Data ────────────────────────────────────────────────

  const completedPhases = investigation?.phases_completed || [];
  const metrics = investigation?.metrics || {};
  const nodes = investigation?.nodes || [];
  const edges = investigation?.edges || [];
  const seedTweet = investigation?.seed_tweet || {};
  const synthesis = investigation?.synthesis || null;
  const botDetection = investigation?.bot_detection || null;
  const volumeData = (investigation as any)?.volume || {};

  // Metrics cards data
  const scopingMetrics = metrics.scoping || {};
  const volumeMetrics = metrics.volume || {};
  const sentimentMetrics = metrics.sentiment_geo || {};

  // ─── Graph Data (React Flow) ─────────────────────────────────────

  const botUserIds = useMemo(() => {
    const ids = new Set<string>();
    const flagged = botDetection?.flagged_accounts || botDetection?.suspicious_accounts || [];
    flagged.forEach((a: any) => {
      const uid = a.user_id ?? a.id;
      const uname = a.username ?? a.userName ?? a.screen_name;
      if (uid != null) {
        ids.add(String(uid));
        ids.add(`user_${uid}`);
      }
      if (uname) {
        ids.add(`user_@${String(uname).replace(/^@/, '')}`);
        ids.add(`user_${String(uname).replace(/^@/, '')}`);
      }
    });
    return ids;
  }, [botDetection]);

  const edgeTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    edges.forEach((e: any) => { const t = e.type || 'unknown'; counts[t] = (counts[t] || 0) + 1; });
    return counts;
  }, [edges]);

  const { flowNodes, flowEdges } = useMemo(() => {
    if (nodes.length === 0) return { flowNodes: [], flowEdges: [] };

    const filteredNodes = graphFilter === 'all'
      ? nodes
      : nodes.filter((n: any) => n.type === graphFilter);
    const filteredNodeIds = new Set(filteredNodes.map((n: any) => n.id));

    let edgePool = edges.filter((e: any) =>
      filteredNodeIds.has(e.source) && filteredNodeIds.has(e.target)
    );
    if (graphEdgeFilter !== 'all') {
      edgePool = edgePool.filter((e: any) => e.type === graphEdgeFilter);
    }

    const connectedIds = new Set<string>();
    edgePool.forEach((e: any) => { connectedIds.add(e.source); connectedIds.add(e.target); });
    let displayNodes: any[] = graphEdgeFilter !== 'all'
      ? filteredNodes.filter((n: any) => connectedIds.has(n.id))
      : filteredNodes;

    // Actual node filters (not just highlights)
    if (botsOnly) {
      displayNodes = displayNodes.filter((n: any) =>
        botUserIds.has(n.id) || (n.metadata?.bot_score ?? n.bot_score ?? 0) > 0.5
      );
    }
    if (sentimentFilter !== 'all') {
      displayNodes = displayNodes.filter((n: any) => {
        const s = n.sentiment_label ?? n.metadata?.sentiment ?? n.sentiment;
        return s === sentimentFilter;
      });
    }

    const authorId = seedTweet?.author?.userId || seedTweet?.author?.id;

    // BFS levels for radial layout
    const bfsLevels = new Map<string, number>();
    if (graphLayout === 'radial' && authorId) {
      const adj = new Map<string, string[]>();
      edgePool.forEach((e: any) => {
        if (!adj.has(e.source)) adj.set(e.source, []);
        if (!adj.has(e.target)) adj.set(e.target, []);
        adj.get(e.source)!.push(e.target);
        adj.get(e.target)!.push(e.source);
      });
      const queue = [authorId];
      bfsLevels.set(authorId, 0);
      while (queue.length) {
        const cur = queue.shift()!;
        const lvl = bfsLevels.get(cur)!;
        for (const nbr of (adj.get(cur) || [])) {
          if (!bfsLevels.has(nbr)) { bfsLevels.set(nbr, lvl + 1); queue.push(nbr); }
        }
      }
    }

    const HIGHLIGHT_COLORS: Record<string, (n: any) => string | null> = {
      bots: (n: any) => botUserIds.has(n.id) || (n.metadata?.bot_score ?? n.bot_score ?? 0) > 0.5 ? '#ff4444' : null,
      sentiment: (n: any) => {
        const s = n.sentiment_label ?? n.metadata?.sentiment ?? n.sentiment;
        if (s === 'positive') return SENTIMENT_COLORS.positive;
        if (s === 'negative') return SENTIMENT_COLORS.negative;
        if (s === 'neutral') return SENTIMENT_COLORS.neutral;
        return null;
      },
    };

    const highlightFn = HIGHLIGHT_COLORS[graphHighlight] || null;
    const cappedNodes = displayNodes.slice(0, 300);
    const N = cappedNodes.length;

    // Pre-compute per-level indices for hierarchical & radial
    const typeLevel: Record<string, number> = { user: 0, post: 1, country: 2 };
    const levelGroups = new Map<number, any[]>();
    cappedNodes.forEach((n: any) => {
      const lvl = graphLayout === 'radial'
        ? (bfsLevels.get(n.id) ?? 3)
        : (typeLevel[n.type] ?? 1);
      if (!levelGroups.has(lvl)) levelGroups.set(lvl, []);
      levelGroups.get(lvl)!.push(n);
    });

    const flowNodes = cappedNodes.map((n: any, i: number) => {
      const nodeType = n.type === 'user' ? 'user' : n.type === 'country' ? 'country' : n.type === 'post' ? 'post' : 'default';
      const highlightColor = highlightFn ? highlightFn(n) : null;
      const isBot = botUserIds.has(n.id) || (n.metadata?.bot_score || 0) > 0.5;
      const isAuthorNode = n.id === authorId || (n.author_username || n.username) === seedTweet?.author?.userName;

      let pos: { x: number; y: number };
      if (graphLayout === 'radial') {
        const level = bfsLevels.get(n.id) ?? 3;
        const RADII = [0, 180, 320, 450, 560];
        const r = RADII[Math.min(level, 4)];
        if (r === 0) { pos = { x: 500, y: 400 }; }
        else {
          const group = levelGroups.get(level) || [];
          const idx = group.findIndex((nn: any) => nn.id === n.id);
          const angle = (idx / group.length) * 2 * Math.PI - Math.PI / 2;
          pos = { x: 500 + r * Math.cos(angle), y: 400 + r * Math.sin(angle) };
        }
      } else if (graphLayout === 'hierarchical') {
        const level = typeLevel[n.type] ?? 1;
        const group = levelGroups.get(level) || [];
        const idx = group.findIndex((nn: any) => nn.id === n.id);
        const spacing = Math.max(900 / (group.length + 1), 100);
        pos = { x: (idx + 1) * spacing, y: 80 + level * 240 };
      } else if (graphLayout === 'grid') {
        const cols = Math.ceil(Math.sqrt(N));
        pos = { x: 80 + (i % cols) * 150, y: 80 + Math.floor(i / cols) * 150 };
      } else {
        // circular (default)
        const angle = ((i + layoutSeed) / N) * 2 * Math.PI + layoutSeed * 0.3;
        const radius = 250 + ((i + layoutSeed * 7) % 40) * 5;
        pos = { x: 400 + radius * Math.cos(angle), y: 400 + radius * Math.sin(angle) };
      }

      return {
        id: n.id,
        type: nodeType,
        position: pos,
        data: {
          label: n.label || n.id,
          username: n.metadata?.username ?? n.username ?? n.author_username,
          isAuthor: isAuthorNode,
          botScore: n.metadata?.bot_score ?? n.bot_score ?? 0,
          text: n.metadata?.text ?? n.text ?? n.label,
          userCount: n.metadata?.user_count,
          highlightColor: isAuthorNode ? '#00ff88' : highlightColor,
          isBot,
          sentiment: n.sentiment_label ?? n.metadata?.sentiment,
        },
        style: isAuthorNode
          ? { boxShadow: '0 0 20px #00ff8888, 0 0 40px #00ff8844', border: '2px solid #00ff88', zIndex: 10 }
          : highlightColor ? { boxShadow: `0 0 12px ${highlightColor}`, border: `2px solid ${highlightColor}` } : undefined,
      };
    });

    const flowEdges = edgePool.slice(0, 500).map((e: any, i: number) => ({
      id: `e-${i}`,
      source: e.source,
      target: e.target,
      type: 'default',
      animated: e.type === 'retweet',
      style: { stroke: EDGE_COLORS[e.type] || '#444', strokeWidth: Math.min(e.weight || 1, 3) },
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_COLORS[e.type] || '#444', width: 12, height: 12 },
      label: e.type,
      labelStyle: { fill: '#666', fontSize: 9 },
    }));

    return { flowNodes, flowEdges };
  }, [nodes, edges, graphFilter, graphEdgeFilter, graphHighlight, seedTweet, botUserIds, layoutSeed, graphLayout, sentimentFilter, botsOnly]);

  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);

  const prevLayoutKey = useRef<string>('');
  useEffect(() => {
    const key = `${flowNodes.length}-${flowEdges.length}-${layoutSeed}`;
    if (key !== prevLayoutKey.current) {
      prevLayoutKey.current = key;
      setRfNodes(flowNodes);
      setRfEdges(flowEdges);
    }
  }, [flowNodes, flowEdges, layoutSeed]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sentiment Charts ────────────────────────────────────────────

  const sentimentPieData = useMemo(() => {
    const overall = sentimentMetrics.sentiment_distribution || sentimentMetrics.sentiment_overall || {};
    return [
      { name: 'Positive', value: overall.positive || 0, color: SENTIMENT_COLORS.positive },
      { name: 'Negative', value: overall.negative || 0, color: SENTIMENT_COLORS.negative },
      { name: 'Neutral', value: overall.neutral || 0, color: SENTIMENT_COLORS.neutral },
    ].filter(d => d.value > 0);
  }, [sentimentMetrics]);

  const countryBarData = useMemo(() => {
    const byCountry = sentimentMetrics.sentiment_by_country || sentimentMetrics.by_country || {};
    return Object.entries(byCountry)
      .map(([country, data]: [string, any]) => ({
        name: country,
        users: data.count || data.user_count || 0,
        positive: data.positive || 0,
        negative: data.negative || 0,
        neutral: data.neutral || 0,
      }))
      .sort((a, b) => b.users - a.users)
      .slice(0, 15);
  }, [sentimentMetrics]);

  const languageData = useMemo(() => {
    const byLang = sentimentMetrics.sentiment_by_language || sentimentMetrics.by_language || {};
    return Object.entries(byLang)
      .map(([lang, data]: [string, any]) => ({
        name: lang.toUpperCase(),
        count: data.count || 0,
        avgSentiment: data.avg_score || data.avg_sentiment || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [sentimentMetrics]);

  const postsBySentiment = useMemo(() => {
    const positive: { id: string; text: string; author: string; score?: number }[] = [];
    const negative: { id: string; text: string; author: string; score?: number }[] = [];
    nodes.forEach((n: any) => {
      if (n.type !== 'post' || !n.sentiment_label) return;
      const text = (n.text || n.label || '').slice(0, 280);
      const author = n.author_username || n.username || '';
      const item = { id: n.id, text, author, score: n.sentiment_score };
      if (n.sentiment_label === 'positive') positive.push(item);
      else if (n.sentiment_label === 'negative') negative.push(item);
    });
    return { positive, negative };
  }, [nodes]);

  const timelineData = useMemo(() => {
    const timeline = volumeMetrics.timeline || [];
    return timeline.map((t: any) => ({
      time: t.hour ? `${t.hour}:00` : t.date || t.label || '',
      engagement: t.engagement || t.count || 0,
    }));
  }, [volumeMetrics]);

  // ─── Word Cloud Data ─────────────────────────────────────────────

  const wordCloudData = useMemo(() => {
    const texts: string[] = [];
    (investigation?.replies || []).forEach((r: any) => { if (r.text || r.fullText) texts.push(r.text || r.fullText); });
    (investigation?.quotes || []).forEach((q: any) => { if (q.text || q.fullText) texts.push(q.text || q.fullText); });
    if (seedTweet?.text || seedTweet?.fullText) texts.push(seedTweet.text || seedTweet.fullText);
    return buildWordCloud(texts);
  }, [investigation, seedTweet]);

  // ─── Timing Analysis Data ──────────────────────────────────────

  const timingData = useMemo(() => {
    const hourBuckets = new Array(24).fill(0);
    const allPosts = [
      ...(investigation?.replies || []),
      ...(investigation?.quotes || []),
    ];
    allPosts.forEach((p: any) => {
      const date = p.createdAt || p.created_at || p.date;
      if (date) {
        const h = new Date(date).getHours();
        if (!isNaN(h)) hourBuckets[h]++;
      }
    });
    return hourBuckets.map((count, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      posts: count,
    }));
  }, [investigation]);

  const postingBursts = useMemo(() => {
    const allPosts = [
      ...(investigation?.replies || []),
      ...(investigation?.quotes || []),
    ];
    const timestamps = allPosts
      .map((p: any) => {
        const d = p.createdAt || p.created_at || p.date;
        return d ? new Date(d).getTime() : 0;
      })
      .filter(t => t > 0)
      .sort((a, b) => a - b);

    const bursts: { start: number; count: number; windowMinutes: number }[] = [];
    if (timestamps.length < 5) return bursts;

    const windowMs = 5 * 60 * 1000;
    let i = 0;
    while (i < timestamps.length) {
      let j = i;
      while (j < timestamps.length && timestamps[j] - timestamps[i] < windowMs) j++;
      const count = j - i;
      if (count >= 5) {
        bursts.push({ start: timestamps[i], count, windowMinutes: 5 });
        i = j;
      } else {
        i++;
      }
    }
    return bursts.slice(0, 10);
  }, [investigation]);

  // ─── Parsed AI Report ──────────────────────────────────────────

  const reportSections = useMemo(() => {
    if (!synthesis) return [];
    const raw = synthesis.ai_report || synthesis.narrative || synthesis.summary || synthesis.report || '';
    return parseAIReport(raw);
  }, [synthesis]);

  // ─── Attribution Data ────────────────────────────────────────────

  const attributionMetrics = metrics.attribution || {};
  const attributionData = (investigation as any)?.attribution || {};
  const coordination = attributionMetrics.coordination || {};
  const suspects = useMemo(() => {
    if (!investigation) return [];
    const botData = investigation.bot_detection || {};
    const flagged = botData.flagged_accounts || botData.suspicious_accounts || [];
    return flagged.sort((a: any, b: any) => (b.bot_score || b.botScore || 0) - (a.bot_score || a.botScore || 0));
  }, [investigation]);

  // ─── Node Type Counts ────────────────────────────────────────────

  const nodeTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    nodes.forEach((n: any) => { counts[n.type] = (counts[n.type] || 0) + 1; });
    return counts;
  }, [nodes]);

  // ─── Render ──────────────────────────────────────────────────────

  const isRunning = activePhase !== null || runningAll;

  return (
    <div className="space-y-6">
      {/* ── Search Bar ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border p-6"
        style={{ background: 'rgba(10,10,10,0.9)', borderColor: NEON_DIM }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${NEON}15`, border: `1px solid ${NEON_DIM}` }}>
            <Zap className="w-5 h-5" style={{ color: NEON }} />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ color: NEON }}>Event Investigation</h2>
            <p className="text-xs opacity-40">Paste a Tweet URL or ID to analyze the event and all participants</p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30" />
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !isRunning && handleStart()}
              placeholder="https://x.com/user/status/123... or tweet ID"
              className="pl-10 bg-black/50 border-gray-800 focus:border-green-500 h-11"
              disabled={isRunning}
            />
          </div>
          <Button
            onClick={handleStart}
            disabled={isRunning || !input.trim()}
            className="h-11 px-6 font-semibold"
            style={{ background: isRunning ? '#222' : NEON, color: '#000' }}
          >
            {activePhase === 1 ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Phase 1
          </Button>
          <Button
            onClick={handleRunAll}
            disabled={isRunning || !input.trim()}
            variant="outline"
            className="h-11 px-6 font-semibold border-green-500/30 hover:bg-green-500/10"
            style={{ color: NEON }}
          >
            {runningAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SkipForward className="w-4 h-4 mr-2" />}
            Run All
          </Button>
        </div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-400"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto"><XCircle className="w-4 h-4" /></button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Previous Investigations ──────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl border overflow-hidden"
        style={{ background: 'rgba(10,10,10,0.9)', borderColor: '#1a1a1a' }}
      >
        <button
          onClick={() => setHistoryOpen(!historyOpen)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <History className="w-4 h-4 opacity-50" />
            <span className="text-sm font-semibold opacity-70">Previous Investigations</span>
            {historyItems.length > 0 && (
              <Badge variant="outline" className="text-[10px] ml-1" style={{ borderColor: NEON_DIM, color: NEON }}>
                {historyItems.length}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 opacity-40 hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); fetchHistory(); }}
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
            {historyOpen ? <ChevronUp className="w-4 h-4 opacity-40" /> : <ChevronDown className="w-4 h-4 opacity-40" />}
          </div>
        </button>

        <AnimatePresence>
          {historyOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-4 border-t border-gray-800/50">
                {historyLoading ? (
                  <div className="flex items-center gap-2 py-6 justify-center text-xs opacity-40">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading investigations...
                  </div>
                ) : historyItems.length === 0 ? (
                  <p className="text-xs opacity-30 py-6 text-center">No previous event investigations found.</p>
                ) : (
                  <div className="space-y-2 mt-3 max-h-[400px] overflow-y-auto pr-1">
                    {historyItems.map((item: any) => (
                      <div
                        key={item.investigation_id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-800/50 hover:border-gray-700 hover:bg-white/[0.02] transition-all group"
                        style={{ background: 'rgba(15,15,15,0.6)' }}
                      >
                        {item.author_avatar ? (
                          <img src={item.author_avatar} className="w-8 h-8 rounded-full flex-shrink-0" alt="" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                            <Users className="w-3.5 h-3.5 opacity-40" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-semibold" style={{ color: '#00aaff' }}>
                              @{item.author || 'unknown'}
                            </span>
                            {item.has_synthesis && (
                              <Badge variant="outline" className="text-[8px] px-1.5 py-0" style={{ borderColor: '#00ff8833', color: NEON }}>
                                Report
                              </Badge>
                            )}
                            {(item.completed_phases || []).length > 0 && (
                              <span className="text-[9px] opacity-30">
                                P{(item.completed_phases || []).join(',')}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] opacity-50 truncate">{item.tweet_text || 'No text'}</p>
                          <div className="flex items-center gap-3 mt-1 text-[9px] opacity-30">
                            <span>{formatNumber(item.likes || 0)} likes</span>
                            <span>{formatNumber(item.retweets || 0)} RTs</span>
                            <span>{formatNumber(item.unique_users || 0)} users</span>
                            {item.reach > 0 && <span>{formatNumber(item.reach)} reach</span>}
                            {item.created_at && <span>{new Date(item.created_at).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            className="h-7 text-[10px] px-3"
                            style={{ background: NEON, color: '#000' }}
                            onClick={() => loadHistoryEntry(item)}
                            disabled={isRunning}
                          >
                            Load
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => deleteHistoryEntry(item.investigation_id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Phase Stepper ──────────────────────────────────────── */}
      {(investigation || activePhase !== null) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PhaseStepper
            phases={PHASES}
            completedPhases={completedPhases}
            activePhase={activePhase}
            onRunPhase={handlePhaseClick}
          />
          {/* Bot Detection Button */}
          {completedPhases.includes(1) && (
            <div className="flex items-center gap-3 mt-3">
              <Button
                onClick={runBotDetection}
                disabled={isRunning}
                variant="outline"
                size="sm"
                className="border-red-500/30 hover:bg-red-500/10 text-red-400"
              >
                {activePhase === 6 ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Bot className="w-3.5 h-3.5 mr-1.5" />}
                Bot Detection
              </Button>
              {botDetection && (
                <Badge variant="outline" style={{ borderColor: '#ff444433', color: '#ff4444' }}>
                  {botDetection.flagged_count || botDetection.suspicious_count || 0} flagged
                </Badge>
              )}
              {/* Export Report Button */}
              <div className="ml-auto">
                <Button
                  onClick={exportReport}
                  disabled={reportExporting || !investigation}
                  variant="outline"
                  size="sm"
                  className="border-cyan-500/30 hover:bg-cyan-500/10 text-cyan-400"
                >
                  {reportExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                  {reportExporting ? 'Generating...' : 'PDF Report'}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Results ────────────────────────────────────────────── */}
      {investigation && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-black/60 border border-gray-800 p-1 mb-6 flex-wrap">
              <TabsTrigger value="overview" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                <Activity className="w-3.5 h-3.5 mr-1.5" />Overview
              </TabsTrigger>
              <TabsTrigger value="network" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                <Network className="w-3.5 h-3.5 mr-1.5" />Network
              </TabsTrigger>
              {completedPhases.includes(2) && (
                <TabsTrigger value="volume" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                  <TrendingUp className="w-3.5 h-3.5 mr-1.5" />Volume
                </TabsTrigger>
              )}
              {completedPhases.includes(3) && (
                <TabsTrigger value="sentiment" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400">
                  <Globe className="w-3.5 h-3.5 mr-1.5" />Sentiment & Geo
                </TabsTrigger>
              )}
              {completedPhases.includes(4) && (
                <TabsTrigger value="attribution" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
                  <Fingerprint className="w-3.5 h-3.5 mr-1.5" />Attribution
                </TabsTrigger>
              )}
              {botDetection && (
                <TabsTrigger value="bots" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
                  <Bot className="w-3.5 h-3.5 mr-1.5" />Bot Detection
                </TabsTrigger>
              )}
              {completedPhases.includes(5) && (
                <TabsTrigger value="synthesis" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                  <Brain className="w-3.5 h-3.5 mr-1.5" />Synthesis
                </TabsTrigger>
              )}
            </TabsList>

            {/* ─── Overview Tab ────────────────────────────────── */}
            <TabsContent value="overview" className="space-y-6">
              <SeedTweetCard tweet={seedTweet} />

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricCard label="Replies" value={scopingMetrics.reply_count || investigation.replies?.length || 0} icon={<MessageSquare className="w-4 h-4" style={{ color: '#00aaff' }} />} color="#00aaff" />
                <MetricCard label="Quotes" value={scopingMetrics.quote_count || investigation.quotes?.length || 0} icon={<Quote className="w-4 h-4" style={{ color: '#ffaa00' }} />} color="#ffaa00" />
                <MetricCard label="Retweeters" value={scopingMetrics.retweeter_count || investigation.retweeters?.length || 0} icon={<Repeat2 className="w-4 h-4" style={{ color: NEON }} />} color={NEON} />
                <MetricCard label="Unique Users" value={scopingMetrics.unique_users || nodeTypeCounts.user || 0} icon={<Users className="w-4 h-4" style={{ color: '#ff00ff' }} />} color="#ff00ff" />
                <MetricCard label="Graph Nodes" value={nodes.length} icon={<Network className="w-4 h-4" style={{ color: '#00ff88' }} />} color="#00ff88" />
                <MetricCard label="Graph Edges" value={edges.length} icon={<Activity className="w-4 h-4" style={{ color: '#aa00ff' }} />} color="#aa00ff" />
              </div>

              {/* Volume Metrics (if available) */}
              {completedPhases.includes(2) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Est. Reach" value={volumeMetrics.estimated_reach || 0} icon={<Eye className="w-4 h-4" style={{ color: '#00aaff' }} />} color="#00aaff" subtitle="Total potential impressions" />
                  <MetricCard label="Engagement" value={volumeMetrics.total_engagement || 0} icon={<Zap className="w-4 h-4" style={{ color: '#ffaa00' }} />} color="#ffaa00" subtitle="Likes + RTs + Replies + Quotes" />
                  <MetricCard label="News Articles" value={volumeMetrics.articles_found || volumeMetrics.news_article_count || 0} icon={<Newspaper className="w-4 h-4" style={{ color: '#ff6600' }} />} color="#ff6600" />
                  <MetricCard label="Trending" value={(volumeMetrics.trending || volumeMetrics.trending_matches)?.length ? 'Yes' : 'No'} icon={<TrendingUp className="w-4 h-4" style={{ color: (volumeMetrics.trending || volumeMetrics.trending_matches)?.length ? NEON : '#666' }} />} color={(volumeMetrics.trending || volumeMetrics.trending_matches)?.length ? NEON : '#666'} />
                </div>
              )}

              {/* Quick Timeline on Overview */}
              {timelineData.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4" style={{ color: NEON }} /> Activity Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={160}>
                      <AreaChart data={timelineData}>
                        <defs>
                          <linearGradient id="overviewGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={NEON} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={NEON} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                        <XAxis dataKey="time" tick={{ fill: '#555', fontSize: 9 }} />
                        <YAxis tick={{ fill: '#555', fontSize: 9 }} />
                        <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                        <Area type="monotone" dataKey="engagement" stroke={NEON} fill="url(#overviewGrad)" strokeWidth={1.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Thread Context */}
              {investigation.thread_tweets && investigation.thread_tweets.length > 1 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Hash className="w-4 h-4" style={{ color: NEON }} />
                      Thread Context ({investigation.thread_tweets.length} tweets)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                      {investigation.thread_tweets.map((t: any, i: number) => (
                        <div key={i} className="flex gap-3 p-3 rounded-lg" style={{ background: 'rgba(20,20,20,0.8)' }}>
                          <div className="w-0.5 rounded-full flex-shrink-0" style={{ background: NEON_DIM }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs leading-relaxed opacity-80">{t.text || t.fullText || ''}</p>
                            <PostMediaGrid mediaUrls={getMediaUrlsFromPost(t)} maxItems={4} className="mt-2" />
                            <div className="flex gap-3 mt-1.5 text-[10px] opacity-40">
                              <span>{formatNumber(t.likeCount || 0)} likes</span>
                              <span>{formatNumber(t.retweetCount || 0)} RTs</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Replies Preview */}
              {investigation.replies && investigation.replies.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-blue-400" />
                      Top Replies ({investigation.replies.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                      {investigation.replies.slice(0, 10).map((r: any, i: number) => (
                        <div key={i} className="p-3 rounded-lg border border-gray-800/50" style={{ background: 'rgba(15,15,25,0.8)' }}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold" style={{ color: '#00aaff' }}>
                              @{r.author?.userName || r.author?.username || 'unknown'}
                            </span>
                            {r.author?.isBlueVerified && <CheckCircle2 className="w-3 h-3 text-blue-400" />}
                            <span className="text-[10px] opacity-30 ml-auto">{formatNumber(r.likeCount || 0)} likes</span>
                          </div>
                          <p className="text-xs opacity-70 line-clamp-2">{r.text || r.fullText || ''}</p>
                          <PostMediaGrid mediaUrls={getMediaUrlsFromPost(r)} maxItems={4} className="mt-2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Quotes Preview */}
              {investigation.quotes && investigation.quotes.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Quote className="w-4 h-4 text-purple-400" />
                      Top Quotes ({investigation.quotes.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                      {investigation.quotes.slice(0, 10).map((q: any, i: number) => (
                        <div key={i} className="p-3 rounded-lg border border-gray-800/50" style={{ background: 'rgba(15,15,25,0.8)' }}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold" style={{ color: '#cc88ff' }}>
                              @{q.author?.userName || q.author?.username || 'unknown'}
                            </span>
                            {q.author?.isBlueVerified && <CheckCircle2 className="w-3 h-3 text-blue-400" />}
                            <span className="text-[10px] opacity-30 ml-auto">{formatNumber(q.likeCount || 0)} likes</span>
                          </div>
                          <p className="text-xs opacity-70 line-clamp-2">{q.text || q.fullText || ''}</p>
                          <PostMediaGrid mediaUrls={getMediaUrlsFromPost(q)} maxItems={4} className="mt-2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ─── Network Tab ─────────────────────────────────── */}
            <TabsContent value="network" className="space-y-4">
              {/* Graph Controls */}
              <div className="rounded-xl border p-4 space-y-3" style={{ background: 'rgba(10,10,10,0.8)', borderColor: '#1a1a1a' }}>

                {/* Layout */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold opacity-60 mr-1">Layout:</span>
                  {[
                    { key: 'circular', label: 'Circular', icon: <Circle className="w-3 h-3 mr-1" /> },
                    { key: 'radial', label: 'Radial', icon: <CircleDot className="w-3 h-3 mr-1" /> },
                    { key: 'hierarchical', label: 'Tree', icon: <Layers className="w-3 h-3 mr-1" /> },
                    { key: 'grid', label: 'Grid', icon: <LayoutGrid className="w-3 h-3 mr-1" /> },
                  ].map(l => (
                    <Button
                      key={l.key}
                      size="sm"
                      variant={graphLayout === l.key ? 'default' : 'outline'}
                      onClick={() => setGraphLayout(l.key as any)}
                      className="text-xs h-7 px-3"
                      style={graphLayout === l.key ? { background: NEON, color: '#000' } : { borderColor: '#333' }}
                    >
                      {l.icon}{l.label}
                    </Button>
                  ))}
                  <div className="ml-auto flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 px-2"
                      style={{ borderColor: '#00ff8844', color: '#00ff88' }}
                      onClick={() => {
                        const seedNode = rfNodes.find(n => n.data?.isAuthor);
                        if (seedNode && reactFlowRef.current) {
                          reactFlowRef.current.fitBounds(
                            { x: seedNode.position.x - 80, y: seedNode.position.y - 80, width: 160, height: 160 },
                            { padding: 1.5, duration: 800 }
                          );
                        }
                      }}
                    >
                      <Locate className="w-3 h-3 mr-1" /> Seed Tweeter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 px-2"
                      style={{ borderColor: '#333' }}
                      onClick={() => setLayoutSeed((s) => s + 1)}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Reshuffle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 px-2"
                      style={{ borderColor: '#333' }}
                      onClick={async () => {
                        if (!graphContainerRef.current) return;
                        try {
                          const domtoimage = (await import('dom-to-image-more')).default;
                          const dataUrl = await domtoimage.toPng(graphContainerRef.current, { quality: 0.95, bgcolor: '#0a0a0a' });
                          const a = document.createElement('a');
                          a.href = dataUrl;
                          a.download = `event-graph-${investigation?.investigation_id || 'export'}.png`;
                          a.click();
                        } catch (e) {
                          console.warn('Export graph failed', e);
                          toast({
                            title: 'Export failed',
                            description: 'Could not export the graph as a PNG. Please try again.',
                            variant: 'destructive',
                          });
                        }
                      }}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Export PNG
                    </Button>
                  </div>
                </div>

                {/* Node type filter */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold opacity-60 mr-1">Nodes:</span>
                  {['all', 'user', 'post', 'country'].map(f => (
                    <Button
                      key={f}
                      size="sm"
                      variant={graphFilter === f ? 'default' : 'outline'}
                      onClick={() => setGraphFilter(f)}
                      className="text-xs h-7 px-3"
                      style={graphFilter === f ? { background: NEON, color: '#000' } : { borderColor: '#333' }}
                    >
                      {f === 'all' ? 'All' : f === 'country' ? 'Countries' : f.charAt(0).toUpperCase() + f.slice(1) + 's'}
                      {f !== 'all' && <span className="ml-1 opacity-60">({nodeTypeCounts[f] || 0})</span>}
                    </Button>
                  ))}
                  {/* Bot filter */}
                  <Button
                    size="sm"
                    variant={botsOnly ? 'default' : 'outline'}
                    onClick={() => setBotsOnly(b => !b)}
                    className="text-xs h-7 px-3"
                    style={botsOnly ? { background: '#ff4444', color: '#fff' } : { borderColor: '#ff444444', color: '#ff6666' }}
                  >
                    <Bot className="w-3 h-3 mr-1" /> Bots Only
                  </Button>
                </div>

                {/* Sentiment filter */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold opacity-60 mr-1">Sentiment:</span>
                  {[
                    { key: 'all', label: 'All', color: '#555' },
                    { key: 'positive', label: 'Positive', color: SENTIMENT_COLORS.positive },
                    { key: 'negative', label: 'Negative', color: SENTIMENT_COLORS.negative },
                    { key: 'neutral', label: 'Neutral', color: SENTIMENT_COLORS.neutral },
                  ].map(s => (
                    <Button
                      key={s.key}
                      size="sm"
                      variant={sentimentFilter === s.key ? 'default' : 'outline'}
                      onClick={() => setSentimentFilter(s.key)}
                      className="text-xs h-7 px-3"
                      style={sentimentFilter === s.key ? { background: s.color, color: '#000' } : { borderColor: `${s.color}44`, color: s.color }}
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>

                {/* Edge Filters */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold opacity-60 mr-1">Edges:</span>
                  {['all', 'reply', 'quote', 'retweet', 'mention', 'follows'].map(f => (
                    <Button
                      key={f}
                      size="sm"
                      variant={graphEdgeFilter === f ? 'default' : 'outline'}
                      onClick={() => setGraphEdgeFilter(f)}
                      className="text-xs h-7 px-3"
                      style={graphEdgeFilter === f ? { background: EDGE_COLORS[f] || NEON, color: '#000' } : { borderColor: '#333', color: EDGE_COLORS[f] || '#aaa' }}
                    >
                      {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                      {f !== 'all' && <span className="ml-1 opacity-60">({edgeTypeCounts[f] || 0})</span>}
                    </Button>
                  ))}
                </div>

                {/* Highlight Mode */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold opacity-60 mr-1">Highlight:</span>
                  {[
                    { key: 'none', label: 'None', icon: <Circle className="w-3 h-3 mr-1" /> },
                    { key: 'bots', label: 'Bot Risk', icon: <Bot className="w-3 h-3 mr-1 text-red-400" /> },
                    { key: 'sentiment', label: 'Sentiment', icon: <Activity className="w-3 h-3 mr-1 text-yellow-400" /> },
                  ].map(h => (
                    <Button
                      key={h.key}
                      size="sm"
                      variant={graphHighlight === h.key ? 'default' : 'outline'}
                      onClick={() => setGraphHighlight(h.key)}
                      className="text-xs h-7 px-3"
                      style={graphHighlight === h.key ? { background: h.key === 'bots' ? '#ff4444' : h.key === 'sentiment' ? '#ffaa00' : NEON, color: '#000' } : { borderColor: '#333' }}
                    >
                      {h.icon}{h.label}
                    </Button>
                  ))}
                  <div className="ml-auto flex items-center gap-2 opacity-40">
                    {Object.entries(EDGE_COLORS).map(([type, color]) => (
                      <span key={type} className="flex items-center gap-1 text-[10px]">
                        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Graph Stats Bar */}
              <div className="flex items-center gap-4 px-2 text-[11px] opacity-50">
                <span>Showing {rfNodes.length} nodes, {rfEdges.length} edges</span>
                {graphEdgeFilter !== 'all' && <Badge variant="outline" className="text-[10px]" style={{ borderColor: EDGE_COLORS[graphEdgeFilter] || '#444', color: EDGE_COLORS[graphEdgeFilter] || '#888' }}>filtered: {graphEdgeFilter}</Badge>}
                {graphHighlight !== 'none' && <Badge variant="outline" className="text-[10px]" style={{ borderColor: '#ffaa0044', color: '#ffaa00' }}>highlighting: {graphHighlight}</Badge>}
              </div>

              {/* React Flow */}
              <div ref={graphContainerRef} className="rounded-xl border overflow-hidden" style={{ height: 600, borderColor: '#222', background: 'rgba(5,5,5,0.95)' }}>
                {rfNodes.length > 0 ? (
                  <ReactFlow
                    nodes={rfNodes}
                    edges={rfEdges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    onInit={(instance) => { reactFlowRef.current = instance; }}
                    fitView
                    minZoom={0.1}
                    maxZoom={3}
                    defaultEdgeOptions={{ type: 'default' }}
                  >
                    <Background color="#111" gap={20} size={1} />
                    <Controls className="bg-black/80 border-gray-800" />
                    <MiniMap
                      nodeColor={(n) => {
                        if (graphHighlight === 'bots' && n.data?.isBot) return '#ff4444';
                        if (graphHighlight === 'sentiment' && n.data?.highlightColor) return n.data.highlightColor;
                        return NODE_COLORS[n.type || 'user'] || '#444';
                      }}
                      maskColor="rgba(0,0,0,0.8)"
                      style={{ background: '#111' }}
                    />
                  </ReactFlow>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm opacity-30">
                    No graph data yet. Run Phase 1 to populate.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ─── Volume Tab ──────────────────────────────────── */}
            <TabsContent value="volume" className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard label="Est. Reach" value={volumeMetrics.estimated_reach || 0} icon={<Eye className="w-4 h-4" style={{ color: '#00aaff' }} />} color="#00aaff" />
                <MetricCard label="Total Engagement" value={volumeMetrics.total_engagement || 0} icon={<Zap className="w-4 h-4" style={{ color: '#ffaa00' }} />} color="#ffaa00" />
                <MetricCard label="News Coverage" value={volumeMetrics.articles_found || volumeMetrics.news_article_count || 0} icon={<Newspaper className="w-4 h-4" style={{ color: '#ff6600' }} />} color="#ff6600" />
                <MetricCard label="Verified Users" value={volumeMetrics.verified_accounts || volumeMetrics.verified_discussion_count || 0} icon={<CheckCircle2 className="w-4 h-4" style={{ color: '#00aaff' }} />} color="#00aaff" />
              </div>

              {/* Timeline Chart */}
              {timelineData.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" style={{ color: NEON }} /> Engagement Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={timelineData}>
                        <defs>
                          <linearGradient id="engGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={NEON} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={NEON} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis dataKey="time" tick={{ fill: '#666', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                        <Area type="monotone" dataKey="engagement" stroke={NEON} fill="url(#engGradient)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Trending Topics */}
              {(volumeMetrics.trending || volumeMetrics.trending_matches || []).length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" style={{ color: NEON }} /> Matching Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {(volumeMetrics.trending || volumeMetrics.trending_matches || []).map((t: any, i: number) => (
                        <Badge key={i} variant="outline" className="px-3 py-1.5 text-xs" style={{ borderColor: NEON_DIM, color: NEON }}>
                          <TrendingUp className="w-3 h-3 mr-1.5" />
                          {typeof t === 'string' ? t : t.name || t.trend || JSON.stringify(t)}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* News Articles */}
              {(volumeData.articles || volumeMetrics.news_articles || []).length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Newspaper className="w-4 h-4 text-orange-400" /> News Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {(volumeData.articles || volumeMetrics.news_articles || []).map((article: any, i: number) => (
                        <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(20,15,5,0.8)', border: '1px solid rgba(255,170,0,0.15)' }}>
                          <p className="text-xs font-semibold text-orange-300 mb-1">{article.title || 'Untitled'}</p>
                          <p className="text-[11px] opacity-50 line-clamp-2">{article.content || article.text || ''}</p>
                          {article.url && (
                            <a href={article.url} target="_blank" rel="noreferrer" className="text-[10px] text-orange-400 opacity-60 hover:opacity-100 flex items-center gap-1 mt-1">
                              <ExternalLink className="w-2.5 h-2.5" /> Read more
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Timing Analysis */}
              {timingData.some(d => d.posts > 0) && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-cyan-400" /> Posting Activity by Hour (UTC)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={timingData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis dataKey="hour" tick={{ fill: '#666', fontSize: 9 }} interval={1} />
                        <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="posts" radius={[3, 3, 0, 0]}>
                          {timingData.map((entry, index) => (
                            <Cell key={index} fill={entry.posts > (Math.max(...timingData.map(d => d.posts)) * 0.7) ? '#ff4444' : entry.posts > (Math.max(...timingData.map(d => d.posts)) * 0.4) ? '#ffaa00' : '#00aaff'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    {postingBursts.length > 0 && (
                      <div className="mt-4 space-y-1.5">
                        <p className="text-[11px] font-semibold text-yellow-400 flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3" /> Detected Activity Bursts
                        </p>
                        {postingBursts.map((b, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px] px-3 py-1.5 rounded" style={{ background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.12)' }}>
                            <Zap className="w-3 h-3 text-yellow-400" />
                            <span className="opacity-70">{new Date(b.start).toLocaleTimeString()}</span>
                            <span className="font-semibold text-yellow-400">{b.count} posts in {b.windowMinutes}min</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Campaign Word Cloud */}
              {wordCloudData.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Type className="w-4 h-4" style={{ color: NEON }} /> Campaign Word Cloud
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-4 min-h-[200px]">
                      {wordCloudData.map((w, i) => {
                        const maxW = wordCloudData[0]?.weight || 1;
                        const ratio = w.weight / maxW;
                        const fontSize = Math.max(10, Math.min(36, 10 + ratio * 26));
                        const opacity = Math.max(0.35, 0.35 + ratio * 0.65);
                        const hue = w.text.startsWith('#') ? 120 : w.text.startsWith('@') ? 200 : 30 + ratio * 90;
                        return (
                          <span
                            key={i}
                            className="cursor-default transition-all hover:scale-110"
                            style={{
                              fontSize: `${fontSize}px`,
                              fontWeight: ratio > 0.5 ? 700 : ratio > 0.3 ? 600 : 400,
                              opacity,
                              color: `hsl(${hue}, 70%, ${50 + ratio * 15}%)`,
                            }}
                            title={`${w.text}: ${w.weight} mentions`}
                          >
                            {w.text}
                          </span>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ─── Sentiment & Geo Tab ─────────────────────────── */}
            <TabsContent value="sentiment" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sentiment Pie */}
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4" style={{ color: NEON }} /> Sentiment Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {sentimentPieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={sentimentPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="value"
                            stroke="none"
                          >
                            {sentimentPieData.map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }}
                            formatter={(value: number, name: string) => [`${value} tweets`, name]}
                          />
                          <Legend
                            formatter={(value: string) => <span style={{ color: '#aaa', fontSize: 11 }}>{value}</span>}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-xs opacity-30">No sentiment data</div>
                    )}
                  </CardContent>
                </Card>

                {/* Language Distribution */}
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Globe className="w-4 h-4 text-yellow-400" /> Language Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {languageData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={languageData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                          <XAxis dataKey="name" tick={{ fill: '#666', fontSize: 10 }} />
                          <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                          <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                          <Bar dataKey="count" fill="#00aaff" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-xs opacity-30">No language data</div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Country Sentiment */}
              {countryBarData.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-yellow-400" /> Sentiment by Country
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={countryBarData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis type="number" tick={{ fill: '#666', fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: '#888', fontSize: 10 }} width={80} />
                        <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                        <Legend formatter={(value: string) => <span style={{ color: '#aaa', fontSize: 11 }}>{value}</span>} />
                        <Bar dataKey="positive" stackId="a" fill={SENTIMENT_COLORS.positive} radius={[0, 0, 0, 0]} />
                        <Bar dataKey="neutral" stackId="a" fill={SENTIMENT_COLORS.neutral} />
                        <Bar dataKey="negative" stackId="a" fill={SENTIMENT_COLORS.negative} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Positive & Negative posts */}
              {(postsBySentiment.positive.length > 0 || postsBySentiment.negative.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-black/40 border-gray-800" style={{ borderLeft: `3px solid ${SENTIMENT_COLORS.positive}` }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2" style={{ color: SENTIMENT_COLORS.positive }}>
                        <Activity className="w-4 h-4" /> Positive posts ({postsBySentiment.positive.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-2">
                        {postsBySentiment.positive.slice(0, 15).map((p) => (
                          <div key={p.id} className="p-3 rounded-lg text-xs" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                            {p.author && <span className="font-semibold opacity-80" style={{ color: SENTIMENT_COLORS.positive }}>@{p.author}</span>}
                            <p className="mt-1 leading-relaxed opacity-90">{p.text || '(no text)'}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-black/40 border-gray-800" style={{ borderLeft: `3px solid ${SENTIMENT_COLORS.negative}` }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2" style={{ color: SENTIMENT_COLORS.negative }}>
                        <Activity className="w-4 h-4" /> Negative posts ({postsBySentiment.negative.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-2">
                        {postsBySentiment.negative.slice(0, 15).map((p) => (
                          <div key={p.id} className="p-3 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                            {p.author && <span className="font-semibold opacity-80" style={{ color: SENTIMENT_COLORS.negative }}>@{p.author}</span>}
                            <p className="mt-1 leading-relaxed opacity-90">{p.text || '(no text)'}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Keywords */}
              {(sentimentMetrics.top_keywords || volumeMetrics.keywords_used || []).length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Hash className="w-4 h-4" style={{ color: NEON }} /> Top Keywords
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {(sentimentMetrics.top_keywords || volumeMetrics.keywords_used || []).map((kw: any, i: number) => {
                        const keyword = typeof kw === 'string' ? kw : kw.keyword || kw.word || '';
                        const count = typeof kw === 'string' ? 0 : kw.count || 0;
                        return (
                          <Badge key={i} variant="outline" className="px-3 py-1.5" style={{ borderColor: '#333', color: '#ccc' }}>
                            {keyword} {count > 0 && <span className="ml-1 opacity-40">({count})</span>}
                          </Badge>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ─── Attribution Tab ─────────────────────────────── */}
            <TabsContent value="attribution" className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard label="Suspects" value={attributionMetrics.suspects_identified || attributionMetrics.top_suspects_count || 0} icon={<Target className="w-4 h-4" style={{ color: '#ff4444' }} />} color="#ff4444" />
                <MetricCard label="Coordination" value={coordination.burst_count || 0} icon={<Zap className="w-4 h-4" style={{ color: '#ffaa00' }} />} color="#ffaa00" subtitle="Timing bursts detected" />
                <MetricCard label="Communities" value={attributionMetrics.communities_discovered || attributionMetrics.communities_found || 0} icon={<Users className="w-4 h-4" style={{ color: '#aa00ff' }} />} color="#aa00ff" />
                <MetricCard label="Follow Links" value={attributionMetrics.follow_relationships_found || attributionMetrics.follow_links_found || 0} icon={<Link2 className="w-4 h-4" style={{ color: '#00ff88' }} />} color="#00ff88" />
              </div>

              {/* Follow Relationships */}
              {(() => {
                const followRels = attributionData.follow_relationships || [];
                if (followRels.length === 0) return null;
                return (
                  <Card className="bg-black/40 border-gray-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Link2 className="w-4 h-4" style={{ color: '#00ff88' }} /> Follow Relationships ({followRels.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="text-left py-2 px-3 font-semibold opacity-60">Follower</th>
                              <th className="text-center py-2 px-1 font-semibold opacity-40">
                                <ArrowRight className="w-3 h-3 inline" />
                              </th>
                              <th className="text-left py-2 px-3 font-semibold opacity-60">Following</th>
                              <th className="text-left py-2 px-3 font-semibold opacity-60">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {followRels.slice(0, 50).map((rel: any, i: number) => (
                              <tr key={i} className="border-b border-gray-900/50 hover:bg-white/[0.02]">
                                <td className="py-2 px-3">
                                  <span className="font-medium" style={{ color: '#00aaff' }}>@{rel.follower || rel.source || '?'}</span>
                                </td>
                                <td className="py-2 px-1 text-center">
                                  <ArrowRight className="w-3 h-3 inline opacity-30" />
                                </td>
                                <td className="py-2 px-3">
                                  <span className="font-medium" style={{ color: NEON }}>@{rel.following || rel.target || '?'}</span>
                                </td>
                                <td className="py-2 px-3">
                                  <Badge variant="outline" className="text-[9px]" style={{ borderColor: '#00ff8833', color: '#00ff88' }}>
                                    {rel.relationship_type || rel.type || 'follows'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {followRels.length > 50 && (
                          <p className="text-[10px] opacity-30 mt-2 px-3">Showing 50 of {followRels.length} relationships</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Communities */}
              {(() => {
                const communities = attributionData.communities || [];
                if (communities.length === 0) return null;
                return (
                  <Card className="bg-black/40 border-gray-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-400" /> Detected Communities ({communities.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {communities.map((comm: any, i: number) => (
                          <div key={i} className="p-4 rounded-lg" style={{ background: 'rgba(20,10,30,0.6)', border: '1px solid rgba(170,0,255,0.15)' }}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-purple-400">Community {i + 1}</span>
                              <Badge variant="outline" className="text-[10px]" style={{ borderColor: '#aa00ff44', color: '#aa00ff' }}>
                                {comm.member_count || comm.members?.length || '?'} members
                              </Badge>
                            </div>
                            {comm.description && <p className="text-xs opacity-60 mb-2">{comm.description}</p>}
                            <div className="flex flex-wrap gap-1.5">
                              {(comm.members || comm.usernames || []).slice(0, 20).map((m: any, j: number) => (
                                <span key={j} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(170,0,255,0.1)', border: '1px solid rgba(170,0,255,0.2)' }}>
                                  @{typeof m === 'string' ? m : m.username || m.name || '?'}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Coordination Signals */}
              {coordination.bursts && coordination.bursts.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" /> Coordination Signals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {coordination.bursts.map((burst: any, i: number) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(20,15,0,0.8)', border: '1px solid rgba(255,170,0,0.15)' }}>
                          <Zap className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-yellow-400">{burst.type || 'Timing Burst'}</p>
                            <p className="text-[10px] opacity-50">{burst.description || `${burst.count || '?'} accounts within ${burst.window || '?'}`}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px]" style={{ borderColor: '#ffaa0044', color: '#ffaa00' }}>
                            {burst.count || '?'} accounts
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Shared Automation / Devices */}
              {coordination.shared_sources && Object.keys(coordination.shared_sources).length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Fingerprint className="w-4 h-4 text-purple-400" /> Shared Automation Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(coordination.shared_sources).map(([source, count]: [string, any]) => (
                        <div key={source} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'rgba(20,10,30,0.8)', border: '1px solid rgba(170,0,255,0.15)' }}>
                          <span className="text-xs truncate mr-2">{source}</span>
                          <Badge variant="outline" className="text-[10px] flex-shrink-0" style={{ borderColor: '#aa00ff33', color: '#aa00ff' }}>{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Spaces */}
              {(() => {
                const spaces = attributionData.spaces || [];
                if (spaces.length === 0) return null;
                return (
                  <Card className="bg-black/40 border-gray-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Globe className="w-4 h-4 text-cyan-400" /> Related Spaces ({spaces.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {spaces.map((sp: any, i: number) => (
                          <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(10,20,20,0.8)', border: '1px solid rgba(0,170,255,0.15)' }}>
                            <Globe className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-cyan-400">{sp.title || sp.name || 'Space'}</p>
                              <p className="text-[10px] opacity-50">{sp.description || `${sp.participant_count || sp.listeners || '?'} participants`}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Top Suspects */}
              {suspects.length > 0 && (
                <Card className="bg-black/40 border-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-400" /> Flagged Accounts ({suspects.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                      {suspects.map((s: any, i: number) => (
                        <SuspectRow key={i} suspect={s} rank={i} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ─── Bot Detection Tab ──────────────────────────── */}
            <TabsContent value="bots" className="space-y-6">
              {botDetection && (
                <>
                  {/* Risk Overview */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MetricCard
                      label="Risk Level"
                      value={botDetection.risk_level || 'Unknown'}
                      icon={<Shield className="w-4 h-4" style={{ color: botDetection.risk_level === 'high' ? '#ff4444' : botDetection.risk_level === 'moderate' ? '#ffaa00' : NEON }} />}
                      color={botDetection.risk_level === 'high' ? '#ff4444' : botDetection.risk_level === 'moderate' ? '#ffaa00' : NEON}
                    />
                    <MetricCard label="Flagged" value={botDetection.flagged_count || botDetection.suspicious_count || 0} icon={<Bot className="w-4 h-4 text-red-400" />} color="#ff4444" />
                    <MetricCard label="Sampled" value={botDetection.total_sampled || 0} icon={<Users className="w-4 h-4 text-blue-400" />} color="#00aaff" />
                    <MetricCard
                      label="Flag Rate"
                      value={`${((botDetection.flagged_percentage || botDetection.suspicious_percentage || 0)).toFixed(1)}%`}
                      icon={<AlertTriangle className="w-4 h-4" style={{ color: '#ffaa00' }} />}
                      color="#ffaa00"
                    />
                  </div>

                  {/* API Flags */}
                  {botDetection.api_flags && (
                    <Card className="bg-black/40 border-gray-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-yellow-400" /> API-Level Flags
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(20,20,20,0.8)' }}>
                            <div className="text-lg font-bold text-yellow-400">{botDetection.api_flags.automated || 0}</div>
                            <div className="text-[10px] opacity-40">Automated</div>
                          </div>
                          <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(20,20,20,0.8)' }}>
                            <div className="text-lg font-bold text-red-400">{botDetection.api_flags.unavailable || 0}</div>
                            <div className="text-[10px] opacity-40">Unavailable</div>
                          </div>
                          <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(20,20,20,0.8)' }}>
                            <div className="text-lg font-bold text-orange-400">{botDetection.api_flags.possibly_sensitive || 0}</div>
                            <div className="text-[10px] opacity-40">Sensitive</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Heuristic Flags */}
                  {botDetection.heuristic_flags && (
                    <Card className="bg-black/40 border-gray-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Fingerprint className="w-4 h-4 text-purple-400" /> Heuristic Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(botDetection.heuristic_flags).map(([flag, count]: [string, any]) => (
                            <div key={flag} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'rgba(15,15,15,0.8)' }}>
                              <span className="text-xs capitalize">{flag.replace(/_/g, ' ')}</span>
                              <span className="text-xs font-bold" style={{ color: count > 5 ? '#ff4444' : count > 2 ? '#ffaa00' : '#888' }}>{count}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Flagged Accounts List */}
                  {suspects.length > 0 && (
                    <Card className="bg-black/40 border-gray-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Bot className="w-4 h-4 text-red-400" /> Flagged Accounts ({suspects.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                          {suspects.map((s: any, i: number) => (
                            <SuspectRow key={i} suspect={s} rank={i} />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* ─── Synthesis Tab ───────────────────────────────── */}
            <TabsContent value="synthesis" className="space-y-6">
              {synthesis && (
                <>
                  {/* Threat Assessment Metrics */}
                  {(synthesis.threat_level || synthesis.amplification_score != null || synthesis.coordination_score != null) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {synthesis.threat_level && (
                        <MetricCard
                          label="Threat Level"
                          value={synthesis.threat_level.toUpperCase()}
                          icon={<Shield className="w-4 h-4" style={{ color: synthesis.threat_level === 'high' ? '#ff4444' : synthesis.threat_level === 'moderate' ? '#ffaa00' : NEON }} />}
                          color={synthesis.threat_level === 'high' ? '#ff4444' : synthesis.threat_level === 'moderate' ? '#ffaa00' : NEON}
                        />
                      )}
                      {synthesis.amplification_score != null && (
                        <MetricCard
                          label="Amplification"
                          value={`${(synthesis.amplification_score * 100).toFixed(0)}%`}
                          icon={<TrendingUp className="w-4 h-4 text-orange-400" />}
                          color="#ff6600"
                        />
                      )}
                      {synthesis.coordination_score != null && (
                        <MetricCard
                          label="Coordination"
                          value={`${(synthesis.coordination_score * 100).toFixed(0)}%`}
                          icon={<Zap className="w-4 h-4 text-yellow-400" />}
                          color="#ffaa00"
                        />
                      )}
                    </div>
                  )}

                  {/* Parsed Report Sections */}
                  {reportSections.length > 0 ? (
                    <div className="space-y-4">
                      {reportSections.map((section, idx) => {
                        const sectionIconMap: Record<string, JSX.Element> = {
                          summary: <FileText className="w-4 h-4" />,
                          findings: <Crosshair className="w-4 h-4" />,
                          sentiment: <Activity className="w-4 h-4" />,
                          attribution: <Fingerprint className="w-4 h-4" />,
                          bot: <Bot className="w-4 h-4" />,
                          media: <Newspaper className="w-4 h-4" />,
                          geo: <MapPin className="w-4 h-4" />,
                          coordination: <Zap className="w-4 h-4" />,
                          recommendations: <Star className="w-4 h-4" />,
                          conclusion: <CheckCircle2 className="w-4 h-4" />,
                          risk: <Shield className="w-4 h-4" />,
                          network: <Network className="w-4 h-4" />,
                          default: <Brain className="w-4 h-4" />,
                        };
                        const sectionColorMap: Record<string, string> = {
                          summary: '#00bbff',
                          findings: NEON,
                          sentiment: '#ffaa00',
                          attribution: '#aa00ff',
                          bot: '#ff4444',
                          media: '#ff6600',
                          geo: '#00aaff',
                          coordination: '#ffaa00',
                          recommendations: '#ffcc00',
                          conclusion: '#00ddaa',
                          risk: '#ff4444',
                          network: '#00ff88',
                          default: '#888',
                        };
                        const icon = sectionIconMap[section.icon] || sectionIconMap.default;
                        const color = sectionColorMap[section.icon] || sectionColorMap.default;
                        const isExecSummary = section.icon === 'summary';

                        return (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.06 }}
                          >
                            <Card className="bg-black/40 border-gray-800 overflow-hidden">
                              <div className="h-0.5" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
                              <CardHeader className="pb-2 pt-4">
                                <CardTitle className="text-sm flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, border: `1px solid ${color}33` }}>
                                    <span style={{ color }}>{icon}</span>
                                  </div>
                                  <span style={{ color }}>{section.title}</span>
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="pb-4">
                                {section.content && (
                                  <div
                                    className={`leading-relaxed mb-3 ${isExecSummary ? 'text-sm' : 'text-xs'}
                                      [&_strong]:font-semibold [&_strong]:text-white
                                      [&_em]:italic [&_em]:opacity-90
                                      [&_p]:my-0.5 [&_p]:leading-relaxed
                                      [&_h1]:hidden [&_h2]:hidden [&_h3]:hidden [&_h4]:hidden`}
                                    style={{ opacity: isExecSummary ? 0.9 : 0.7 }}
                                  >
                                    <ReactMarkdown>{section.content}</ReactMarkdown>
                                  </div>
                                )}
                                {section.bullets.length > 0 && (
                                  <div className="space-y-2">
                                    {section.bullets.map((bullet, bi) => (
                                      <div key={bi} className="flex gap-2.5 items-start pl-1">
                                        <ChevronRight className="w-3 h-3 flex-shrink-0 mt-1 opacity-40" style={{ color }} />
                                        <div className="text-xs leading-relaxed opacity-80
                                          [&_strong]:font-semibold [&_strong]:text-white
                                          [&_p]:inline [&_p]:my-0 [&_em]:italic">
                                          <ReactMarkdown>{bullet}</ReactMarkdown>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Key Findings fallback */
                    (synthesis.key_findings || synthesis.structured?.implications) && (
                      <Card className="bg-black/40 border-gray-800">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Brain className="w-4 h-4" style={{ color: NEON }} /> Key Findings
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {(Array.isArray(synthesis.key_findings || synthesis.structured?.implications) ? (synthesis.key_findings || synthesis.structured?.implications) : [synthesis.key_findings || synthesis.structured?.implications]).map((finding: any, i: number) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="flex gap-3 p-3 rounded-lg"
                                style={{ background: 'rgba(0,255,0,0.03)', border: `1px solid ${NEON_DIM}` }}
                              >
                                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: NEON }} />
                                <p className="text-sm leading-relaxed">{typeof finding === 'string' ? finding : JSON.stringify(finding)}</p>
                              </motion.div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  )}

                  {/* Recommendations */}
                  {synthesis.recommendations && (
                    <Card className="bg-black/40 border-gray-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-400" /> Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {(Array.isArray(synthesis.recommendations) ? synthesis.recommendations : [synthesis.recommendations]).map((rec: any, i: number) => (
                            <div key={i} className="flex gap-3 p-3 rounded-lg" style={{ background: 'rgba(30,20,0,0.6)', border: '1px solid rgba(255,170,0,0.15)' }}>
                              <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5 text-yellow-400" />
                              <p className="text-sm leading-relaxed opacity-80">{typeof rec === 'string' ? rec : JSON.stringify(rec)}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Fallback for empty synthesis */}
                  {!synthesis.key_findings && !synthesis.ai_report && !synthesis.narrative && !synthesis.summary && !synthesis.report && (
                    <Card className="bg-black/40 border-gray-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Brain className="w-4 h-4" style={{ color: NEON }} /> Synthesis Data
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <pre className="text-xs font-mono whitespace-pre-wrap opacity-60 max-h-[500px] overflow-y-auto">
                          {JSON.stringify(synthesis, null, 2)}
                        </pre>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* ── Similar Narratives ─────────────────────────── */}
              <Card className="bg-black/40 border-gray-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ScanSearch className="w-4 h-4 text-purple-400" /> Similar Narratives
                    <span className="text-xs opacity-40 font-normal ml-1">Find accounts spreading the same story</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto text-xs h-7 px-3"
                      style={{ borderColor: '#a855f744', color: '#a855f7' }}
                      disabled={loadingSimilar || !investigation?.investigation_id}
                      onClick={async () => {
                        if (!investigation?.investigation_id) return;
                        setLoadingSimilar(true);
                        try {
                          const res = await apiClient.findSimilarNarratives(investigation.investigation_id) as any;
                          setSimilarNarratives(res);
                        } catch (e) {
                          console.warn('Similar narratives failed', e);
                        } finally {
                          setLoadingSimilar(false);
                        }
                      }}
                    >
                      {loadingSimilar ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ScanSearch className="w-3 h-3 mr-1" />}
                      {loadingSimilar ? 'Searching…' : 'Find Similar'}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {similarNarratives && (
                  <CardContent className="space-y-3">
                    {similarNarratives.narrative_context && (
                      <p className="text-xs opacity-50 font-mono border border-gray-800 rounded p-2">
                        Themes: {similarNarratives.narrative_context}
                      </p>
                    )}
                    {(similarNarratives.similar_tweets?.posts || []).slice(0, 15).map((post: any, i: number) => (
                      <div key={i} className="rounded-lg p-3 space-y-1" style={{ background: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.15)' }}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-purple-400">@{post.author?.userName || post.username || 'unknown'}</span>
                          {post.author?.followers && <span className="text-[10px] opacity-40">{post.author.followers.toLocaleString()} followers</span>}
                          {post.url && (
                            <a href={post.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-[10px] opacity-40 hover:opacity-80">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs opacity-70 leading-relaxed">{post.text || post.full_text || ''}</p>
                        {(post.likes != null || post.retweets != null) && (
                          <div className="flex gap-3 text-[10px] opacity-40">
                            {post.likes != null && <span>♥ {post.likes}</span>}
                            {post.retweets != null && <span>↺ {post.retweets}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                    {(similarNarratives.similar_tweets?.posts?.length === 0) && (
                      <p className="text-xs opacity-40 text-center py-4">No similar narratives found</p>
                    )}
                  </CardContent>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      )}

      {/* ── Empty State ────────────────────────────────────────── */}
      {!investigation && !isRunning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${NEON}10`, border: `1px solid ${NEON_DIM}` }}>
            <Zap className="w-10 h-10" style={{ color: NEON, opacity: 0.5 }} />
          </div>
          <h3 className="text-lg font-semibold mb-2 opacity-60">Event Investigation</h3>
          <p className="text-sm opacity-30 max-w-md">
            Paste a Tweet URL or ID above to analyze the event. The system will collect replies, quotes, retweeters,
            then run multi-phase enrichment including volume analysis, sentiment scoring, geo clustering, attribution detection, and AI synthesis.
          </p>
          <div className="flex items-center gap-6 mt-8">
            {PHASES.map(p => (
              <div key={p.id} className="flex flex-col items-center gap-1.5 opacity-30">
                {p.icon}
                <span className="text-[10px]">{p.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* ── Loading State ──────────────────────────────────────── */}
      {isRunning && !investigation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-20"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ border: `2px solid ${NEON}`, boxShadow: `0 0 30px ${NEON_GLOW}` }}
          >
            <Loader2 className="w-8 h-8" style={{ color: NEON }} />
          </motion.div>
          <p className="text-sm font-semibold" style={{ color: NEON }}>
            Running Phase {activePhase}...
          </p>
          <p className="text-xs opacity-30 mt-1">
            {PHASES.find(p => p.id === activePhase)?.description || 'Processing...'}
          </p>
        </motion.div>
      )}
    </div>
  );
}
